// ============================================================================
// Curated Starter BOQ Templates — single source of truth for cross-app DTOs
// ============================================================================

export const BOQ_STARTER_TEMPLATE_SLUGS = [
	"full-home-interiors",
	"modular-kitchen",
	"wardrobe-carpentry",
	"electrical-works",
	"false-ceiling-lighting",
	"painting",
	"flooring-tiling",
	"bathroom-renovation",
] as const;

export const BOQ_STARTER_TIERS = ["standard", "premium", "luxury"] as const;

export const BOQ_STARTER_PROVIDER_ROLES = ["designer", "contractor"] as const;

export const BOQ_STARTER_PROJECT_SCOPES = ["full_home", "partial"] as const;

export const BOQ_QUANTITY_RULE_KINDS = [
	"fixed",
	"area_multiplier",
	"room_count",
	"scope_room_count",
] as const;

export const BOQ_PRICE_RULE_KINDS = ["catalog_price", "fixed"] as const;

export const BOQ_STARTER_WARNING_CODES = [
	"catalog_product_inactive",
	"catalog_variant_missing",
	"quantity_fallback",
	"price_fallback",
] as const;

// Stable error codes returned by the share-template API. Agents and tests
// match on these instead of the human-readable `message`.
export const BOQ_STARTER_ERROR_CODES = [
	"template_not_found",
	"template_role_forbidden",
	"template_inactive",
	"catalog_empty",
	"template_unpriced",
	"malformed_rule",
	"missing_uom",
] as const;

/**
 * Why a `catalog_price` line has no customer price.
 *
 * - `no_price`: no published price is in effect for the product.
 * - `no_hsn_code`: a price is in effect, but neither the product nor its
 *   category has an HSN/SAC code, so no GST rate can be looked up.
 * - `no_gst_rate`: a price is in effect, but no GST rate for its HSN/SAC code
 *   covers the price's date.
 * - `rate_not_yet_effective`: GST rates for the code start only after the
 *   price's date.
 * - `basis_not_shown`: the price is on a basis stored for reference only.
 * - `price_not_refreshed`: a price is in effect and would be shown now, but
 *   the product's customer price was worked out before its price took
 *   effect or before its HSN/SAC code or GST rate last changed.
 */
export const BOQ_STARTER_UNPRICED_REASONS = [
	"no_price",
	"no_hsn_code",
	"no_gst_rate",
	"rate_not_yet_effective",
	"basis_not_shown",
	"price_not_refreshed",
] as const;

export type BoqStarterTemplateSlug =
	(typeof BOQ_STARTER_TEMPLATE_SLUGS)[number];
export type BoqStarterTier = (typeof BOQ_STARTER_TIERS)[number];
export type BoqStarterProviderRole =
	(typeof BOQ_STARTER_PROVIDER_ROLES)[number];
export type BoqStarterProjectScope =
	(typeof BOQ_STARTER_PROJECT_SCOPES)[number];
export type BoqQuantityRuleKind = (typeof BOQ_QUANTITY_RULE_KINDS)[number];
export type BoqPriceRuleKind = (typeof BOQ_PRICE_RULE_KINDS)[number];
export type BoqStarterWarningCode = (typeof BOQ_STARTER_WARNING_CODES)[number];
export type BoqStarterErrorCode = (typeof BOQ_STARTER_ERROR_CODES)[number];
export type BoqStarterUnpricedReason =
	(typeof BOQ_STARTER_UNPRICED_REASONS)[number];

// Discriminated union for quantity rules. Matches what the engine actually
// switches on (todo 001) and what type guards verify at read time (todo 006).
export type BoqQuantityRule =
	| {
			kind: "fixed";
			value: number;
	  }
	| {
			kind: "area_multiplier";
			source: "property.sizeSqft";
			multiplier: number;
			fallbackQuantity: number;
			warningWhenFallback?: boolean;
	  }
	| {
			kind: "room_count";
			source: "property.rooms.bedrooms" | "property.rooms.bathrooms";
			multiplier?: number;
			fallbackQuantity: number;
			warningWhenFallback?: boolean;
	  }
	| {
			kind: "scope_room_count";
			source: "project.scopeDetails.rooms";
			multiplier?: number;
			fallbackQuantity: number;
			warningWhenFallback?: boolean;
	  };

/**
 * How a template line gets its unit price.
 *
 * `catalog_price` replaces `variant_mrp`, `product_mrp` and
 * `indicative_midpoint` (plan 004). Those were three ways of reading the legacy
 * price mirror, and they made a template author choose between sources whose GST
 * treatment they had no way to know. `indicative_midpoint` went further and
 * averaged two figures of unstated basis into a third with no basis at all.
 *
 * There is now one catalogue answer per (product, variant), normalised to
 * GST-inclusive, so that choice no longer exists to be made wrongly. The
 * variant-then-product fallback these encoded lives in the pricing service.
 */
export type BoqPriceRule =
	| { kind: "fixed"; value: number }
	| { kind: "catalog_price" };

export interface BoqStarterTemplate {
	id: string;
	name: string;
	slug: string;
	description: string | null;
	projectScope: BoqStarterProjectScope | null;
	supportedProviderRoles: BoqStarterProviderRole[];
	defaultTier: BoqStarterTier;
	isActive: boolean;
	sortOrder: number;
}

export interface BoqStarterPreviewItem {
	templateItemId: string;
	catalogProductId: string;
	catalogVariantId: string | null;
	description: string;
	uom: string | null;
	quantity: number;
	unitPrice: number;
	/**
	 * The price observation `unitPrice` came from, or null for a `fixed` rule.
	 *
	 * A BOQ is quoted at a moment and read later, by which time the catalogue has
	 * moved on. Without this, a line and today's product page disagree and nothing
	 * records which was right -- so the line can cite the document behind its own
	 * number rather than asking anyone to take it on trust.
	 */
	priceObservationId: string | null;
	lineTotal: number;
	assumptions: string[];
	warnings: string[];
}

export interface BoqStarterPreviewSection {
	templateSectionId: string;
	sectionName: string;
	description: string | null;
	lineNo: number;
	sectionTotal: number;
	items: BoqStarterPreviewItem[];
}

/** Why one line has no price, with what the wording needs to name it. */
export interface BoqStarterUnpriced {
	reason: BoqStarterUnpricedReason;
	/** The HSN/SAC code a rate was looked up for; null unless the reason is about a rate. */
	hsnCode: string | null;
	/** For `rate_not_yet_effective`, when the earliest later rate starts (ISO). */
	rateEffectiveFrom: string | null;
}

export interface BoqStarterWarning {
	code: BoqStarterWarningCode;
	message: string;
	templateItemId?: string;
	catalogProductId?: string;
	catalogVariantId?: string | null;
	/** The catalogue product's name, set on `price_fallback`. */
	productName?: string;
	/** Set on `price_fallback` for a `catalog_price` line. */
	unpriced?: BoqStarterUnpriced;
}

/** Lines sharing one reason (and HSN/SAC code), in a `template_unpriced` error. */
export interface BoqStarterUnpricedGroup extends BoqStarterUnpriced {
	lines: number;
}

/** The `data` of a `template_unpriced` error. */
export interface BoqStarterUnpricedSummary {
	/** Lines skipped for want of a price. */
	unpricedLines: number;
	/** Lines skipped because their product or variant is unavailable. */
	unavailableLines: number;
	groups: BoqStarterUnpricedGroup[];
}

export interface BoqStarterAssumption {
	label: string;
	value: string;
	source: string;
}

export interface BoqStarterPreview {
	template: BoqStarterTemplate;
	sections: BoqStarterPreviewSection[];
	assumptions: BoqStarterAssumption[];
	warnings: BoqStarterWarning[];
	skippedItems: BoqStarterWarning[];
	estimatedTotal: number;
}

export interface BoqStarterGenerationRun {
	id: string;
	boqId: string;
	templateId: string;
	createdBy: string;
	inputSnapshot: Record<string, unknown>;
	assumptions: BoqStarterAssumption[];
	warnings: BoqStarterWarning[];
	skippedItems: BoqStarterWarning[];
	createdAt: string;
}

export interface GenerateBoqFromStarterInput {
	title?: string;
	notes?: string;
}

export interface GenerateBoqFromStarterResponse {
	boq: {
		id: string;
		projectId: string;
		versionNumber: number;
		status: string;
		title?: string | null;
		grandTotal?: number | null;
		generationRunId?: string | null;
	};
	generationRun: BoqStarterGenerationRun;
	preview: BoqStarterPreview;
}

// Query-param shape for listing templates (todo 019).
export interface ListBoqStarterTemplatesQuery {
	tier?: BoqStarterTier;
	scope?: BoqStarterProjectScope;
	slug?: BoqStarterTemplateSlug;
	limit?: number;
	cursor?: string;
}

export interface ListBoqStarterTemplatesResponse {
	templates: BoqStarterTemplate[];
	nextCursor: string | null;
}

// ----------------------------------------------------------------------------
// Type guards (used by the backend service to validate JSONB rule columns at
// read time — todo 006).
// ----------------------------------------------------------------------------

const QUANTITY_RULE_KIND_SET: ReadonlySet<string> = new Set(
	BOQ_QUANTITY_RULE_KINDS,
);
const PRICE_RULE_KIND_SET: ReadonlySet<string> = new Set(BOQ_PRICE_RULE_KINDS);

export function isBoqQuantityRule(value: unknown): value is BoqQuantityRule {
	if (!value || typeof value !== "object") return false;
	const r = value as { kind?: unknown };
	return typeof r.kind === "string" && QUANTITY_RULE_KIND_SET.has(r.kind);
}

export function isBoqPriceRule(value: unknown): value is BoqPriceRule {
	if (!value || typeof value !== "object") return false;
	const r = value as { kind?: unknown };
	return typeof r.kind === "string" && PRICE_RULE_KIND_SET.has(r.kind);
}

export function isBoqStarterTier(value: unknown): value is BoqStarterTier {
	return (
		typeof value === "string" &&
		(BOQ_STARTER_TIERS as readonly string[]).includes(value)
	);
}
